import type { Layouts } from 'react-grid-layout'
import type { DeviceType } from '~/types/device-type'
import type {
  IChartConfigApiModel,
  IGetSeriesDataBody,
  IChartConfigApiModelPayload,
  CA_PAGES,
  IBuilderConfig,
  IGetFieldsPayload,
  BUSINESS_REPORTS_PAGES,
} from '~/types/graph-builder'
import type { IPageLayout } from '~/types/graph-layout'

import config from '~/config'
import { request } from '~/utils/request'

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/graph-data/one */
export const getSeriesData = <T>(body: IGetSeriesDataBody, mode: string): Promise<T> => {
  return request({
    data: {
      ...body,
      mode,
    },
    method: 'POST',
    url: `${config.ANALYTICS_API}/graph-data/one`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/graph-data/batch */
export const getSeriesBatchData = <T>(body: IGetSeriesDataBody[], mode: string): Promise<T> => {
  return request({
    data: body.map((el) => ({ ...el, mode })),
    method: 'POST',
    url: `${config.ANALYTICS_API}/graph-data/batch`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#get-/graph-configs/-id- */
export const getFields = ({ appKey, featureName }: IGetFieldsPayload): Promise<string[]> => {
  return request({
    data: {
      app_key: appKey,
      feature_name: featureName,
    },
    method: 'POST',
    url: `${config.ANALYTICS_API}/graph-configs/fields`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#get-/graph-configs/-id- */
export const getChartConfig = (configId: string): Promise<IChartConfigApiModel> => {
  return request({
    url: `${config.ANALYTICS_API}/graph-configs/${configId}`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#get-/graph-configs */
export const listChartConfigs = ({
  appKey,
  deviceType,
  page,
  pageName,
  size,
}: {
  appKey?: string
  deviceType?: string
  page?: number
  pageName?: CA_PAGES | BUSINESS_REPORTS_PAGES
  size?: number
}): Promise<IPaginatedResponse<IChartConfigApiModel>> => {
  const queryParams = [
    `size=${size ?? 1000}`,
    ...(pageName ? [`page-name=${pageName}`] : []),
    ...(appKey ? [`app-key=${appKey}`] : []),
    ...(deviceType ? [`device-type=${deviceType}`] : []),
    ...(page ? [`page=${page}`] : []),
  ]

  return request({
    url: `${config.ANALYTICS_API}/graph-configs?${queryParams.join('&')}`,
  })
}

const formatData = (params: IBuilderConfig): IChartConfigApiModelPayload => {
  const applications = params.applications.map((value) => {
    const [appKey, deviceType] = value.split(':')
    return {
      app_key: appKey,
      device_type: deviceType as DeviceType,
    }
  })

  return {
    applications,
    column: params.column || 0,
    feature_name: params.featureName,
    force_kpi_field: params.forceKPIField,
    how: params.tooltip?.how,
    line: params.line || 0,
    mode: params.mode,
    nb_last_days_to_ignore: params.nbLastDaysToIgnore,
    page: params.page,
    position: params.position || 0,
    related_glossary_items: params.tooltip?.relatedGlossaryItems,
    required_breakdown: params.requiredBreakdown,
    section: params.section,
    series: params.series.map((el) => ({
      default_breakdown: el.defaultBreakdown,
      graph_type: el.graphType,
      min_nbu: el.minNbu || 0,
      normalized_by: el.normalizedBy,
      values: el.values,
      with_inf: el.withInf || false,
      with_sup: el.withSup || false,
      with_trend: el.withTrend || false,
      y_axes_name: el.yAxisName || '',
    })),
    title: params.title,
    want_negative_evolution: params.wantNegativeEvolution || false,
    what: params.tooltip?.what,
    y_axes: params.yAxes.map((el) => ({
      name: el.name || '',
      precision: el.precision,
      unit: el.unit,
    })),
  }
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/graph-configs */
export const createChartConfig = (params: IBuilderConfig): Promise<IChartConfigApiModel> => {
  const payload: IChartConfigApiModelPayload = formatData(params)

  return request({
    data: payload,
    method: 'POST',
    url: `${config.ANALYTICS_API}/graph-configs`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#delete-/graph-configs/-id- */
export const deleteChartConfig = ({ configId }: { configId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.ANALYTICS_API}/graph-configs/${configId}`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#put-/graph-configs/-id- */
export const updateChartConfig = ({ configId, ...params }: IBuilderConfig & { configId: string }): Promise<void> => {
  const payload: IChartConfigApiModelPayload = formatData(params)
  return request({
    data: payload,
    method: 'PUT',
    url: `${config.ANALYTICS_API}/graph-configs/${configId}`,
  })
}

/** @doc https://analytics-prod-europe-west9-6xd26izywq-od.a.run.app/#get-/graph/page/-page- */
export const listPageLayouts = ({ page }: { page: CA_PAGES | BUSINESS_REPORTS_PAGES }): Promise<IPageLayout[]> => {
  return request({
    url: `${config.ANALYTICS_API}/graph/page/${page}`,
  })
}

/** @doc https://analytics-prod-europe-west9-6xd26izywq-od.a.run.app/#get-/graph/page/-page- */
export const getPageLayout = ({
  page,
  section,
}: {
  page: CA_PAGES | BUSINESS_REPORTS_PAGES
  section?: string
}): Promise<IPageLayout> => {
  return request({
    url: `${config.ANALYTICS_API}/graph/page/${page}/section/${section}`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#post-/graph/page */
export const batchCreatePageLayout = (data: {
  page: CA_PAGES | BUSINESS_REPORTS_PAGES
  sections: { layout: Layouts; section_name?: string }[]
}): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.ANALYTICS_API}/graph/page`,
  })
}

/** @doc https://analytics-prod-europe-west9-6xd26izywq-od.a.run.app/#put-/graph/page/-page- */
export const updatePageLayout = ({
  layout,
  page,
  section,
}: {
  layout: Layouts
  page: CA_PAGES | BUSINESS_REPORTS_PAGES
  section?: string
}): Promise<IPageLayout> => {
  return request({
    data: { layout, section },
    method: 'PUT',
    url: `${config.ANALYTICS_API}/graph/page/${page}`,
  })
}

/** @doc https://analytics-dev-europe-west9-6xd26izywq-od.a.run.app/#delete-/graph/page/-page-/section/-section- */
export const deletePageLayout = ({
  page,
  section,
}: {
  page: CA_PAGES | BUSINESS_REPORTS_PAGES
  section: string
}): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.ANALYTICS_API}/graph/page/${page}/section/${section}`,
  })
}
